<template>
  <div class="survey-preview-container">
    <!-- Loading state -->
    <div
      v-if="loading"
      class="text-center p-4"
    >
      <div
        class="spinner-border"
        role="status"
      >
        <span class="sr-only">Loading...</span>
      </div>
      <p
        class="mt-2"
      >
        {{ $tk('ClientPortal.Survey.LoadingPreview') }}
      </p>
    </div>

    <!-- Error state -->
    <div
      v-else-if="error"
      class="alert alert-danger m-4"
    >
      <h5>Error loading preview</h5>
      <p>{{ error.message || error }}</p>
      <button
        class="btn btn-primary"
        @click="loadPreview"
      >
        Try Again
      </button>
    </div>

    <!-- Survey preview HTML from server -->
    <div
      v-else-if="previewHtml"
      id="survey-preview-wrapper"
      :class="[
        'survey-preview-content',
        { 'survey-preview-content-scroll': calculatedSurveyType === 3 }
      ]"
    >
      <iframe
        ref="previewIframe"
        :style="iframeStyle"
        frameborder="0"
        scrolling="no"
        class="survey-preview-iframe"
      />
    </div>

    <div
      v-else
      class="text-center p-4"
    >
      <p>No preview available</p>
    </div>
  </div>
</template>

<script>
import SurveysService from '@/api/client/surveys.service'

export default {
  name: 'SurveyPreview',
  props: {
    surveyWrapperPreviewModel: {
      type: Object,
      default: () => ({}),
    },
    surveyConfiguration: {
      type: Object,
      default: () => ({}),
    },
    surveyType: {
      type: Number,
      default: 1, // 1 = Square, 2 = Skyscraper, 3 = Leaderboard
    },
  },
  data() {
    return {
      loading: false,
      error: null,
      previewHtml: '',
    }
  },
  computed: {
    calculatedSurveyType() {
      if (!this.surveyWrapperPreviewModel) {
        return this.surveyType || 1
      }

      const width = this.surveyWrapperPreviewModel.width || 300
      const height = this.surveyWrapperPreviewModel.height || 250

      if (width === 170 && height === 600) {
        return 2 // Skyscraper
      }
      if (width === 728 && height === 90) {
        return 3 // Leaderboard
      }
      if (width === 300 && height === 600) {
        return 4 // Half Page
      }
      return 1 // Square (default)
    },

    iframeStyle() {
      const dimensions = this.getSurveyDimensions()
      return {
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`,
        border: 'none',
        display: 'block',
        margin: '0 auto',
      }
    },
  },
  watch: {
    surveyWrapperPreviewModel: {
      handler(newModel) {
        if (newModel && Object.keys(newModel).length > 0) {
          this.loadPreview()
        }
      },
      immediate: true,
      deep: true,
    },
    calculatedSurveyType: {
      handler() {
        if (this.surveyWrapperPreviewModel && Object.keys(this.surveyWrapperPreviewModel).length > 0) {
          this.loadPreview()
        }
      },
    },
  },
  mounted() {
    this.loadPreview()
  },
  methods: {
    async loadPreview() {
      if (!this.surveyWrapperPreviewModel || Object.keys(this.surveyWrapperPreviewModel).length === 0) {
        return
      }

      this.loading = true
      this.error = null

      try {
        const surveyModel = this.transformToSurveyWrapperModel(this.surveyWrapperPreviewModel)

        const response = await SurveysService.getSurveyPreviewHtml(surveyModel, this.calculatedSurveyType)

        this.previewHtml = this.processHtmlResponse(response)

        this.$nextTick(() => {
          this.renderInIframe()
        })
      } catch (error) {
        console.error('Error loading survey preview:', error)
        this.error = error.response?.data?.message || error.message || this.$tk('ClientPortal.Survey.Error.FailedToLoadSurveyPreview')
      } finally {
        this.loading = false
      }
    },

    transformToSurveyWrapperModel(previewModel) {
      return {
        id: previewModel.id || 0,
        name: previewModel.name || '',
        endImageDestinationURL: previewModel.endImageDestinationURL || '',
        accountLogo: previewModel.accountLogo || '',
        surveyColors: previewModel.surveyColors || {},
        isOldVersion: previewModel.isOldVersion || false,
        endMessage: previewModel.endMessage || previewModel.endingMessage || '',
        mediaUrl: previewModel.mediaUrl || '',
        showQuestionsNumber: previewModel.showQuestionsNumber || false,
        hasClickableLink: previewModel.hasClickableLink || false,
        buttonText: previewModel.buttonText || '',
        destinationURL: previewModel.destinationURL || '',
        rewardCode: previewModel.rewardCode || '',
        rewardMessage: previewModel.rewardMessage || '',
        hasRewardMessage: previewModel.hasRewardMessage || false,
        surveyResponse: previewModel.surveyResponse || null,
        questions: previewModel.questions || [],
        brandName: previewModel.brandName || '',
        brandText: previewModel.brandText || '',
        brandUrl: previewModel.brandUrl || '',
        width: previewModel.width || 300,
        height: previewModel.height || 250,
      }
    },

    processHtmlResponse(html) {
      const centralBaseUrl = process.env.VUE_APP_CENTRAL_API_URL || 'http://localhost:51524'
      console.log(centralBaseUrl)
      const baseTag = `<base href="${centralBaseUrl}/">`
      html = html.replace(/<base[^>]*>/gi, '')
      if (html.includes('<head>')) {
        html = html.replace('<head>', `<head>${baseTag}`)
      } else if (html.includes('<HEAD>')) {
        html = html.replace('<HEAD>', `<HEAD>${baseTag}`)
      } else {
        html = `${baseTag}${html}`
      }
      return html
    },

    renderInIframe() {
      if (!this.$refs.previewIframe || !this.previewHtml) {
        return
      }

      const iframe = this.$refs.previewIframe
      const doc = iframe.contentDocument || iframe.contentWindow.document

      doc.open()
      doc.write(this.previewHtml)
      doc.close()

      iframe.onload = () => {
        try {
          const contentHeight = doc.body.scrollHeight
          if (contentHeight > 0) {
            iframe.style.height = `${contentHeight}px`
          }
        } catch (e) {
          console.log('Could not auto-resize iframe:', e.message)
        }
      }
    },

    getSurveyDimensions() {
      switch (this.calculatedSurveyType) {
        case 1:
          return { width: 300, height: 250 }
        case 2:
          return { width: 170, height: 600 }
        case 3:
          return { width: 728, height: 90 }
        case 4:
          return { width: 300, height: 600 }
        default:
          return { width: 300, height: 250 }
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.survey-preview-container {
  width: 100%;
  height: 100%;
  min-height: 400px;
}

.survey-preview-content-scroll {
  overflow-x: scroll;
  min-height: 100px;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
  border: 0.25em solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spinner-border 0.75s linear infinite;
}

@keyframes spinner-border {
  to {
    transform: rotate(360deg);
  }
}

.alert {
  border-radius: 0.25rem;
  padding: 0.75rem 1.25rem;
  margin-bottom: 1rem;
  border: 1px solid transparent;
}

.alert-danger {
  color: #721c24;
  background-color: #f8d7da;
  border-color: #f5c6cb;
}

.btn {
  padding: 0.375rem 0.75rem;
  border: 1px solid transparent;
  border-radius: 0.25rem;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
  font-weight: 400;
  text-align: center;
  vertical-align: middle;
  user-select: none;
}

.btn-primary {
  color: #fff;
  background-color: #007bff;
  border-color: #007bff;
}

.btn-primary:hover {
  background-color: #0056b3;
  border-color: #004085;
}

.text-center {
  text-align: center;
}

.p-4 {
  padding: 1.5rem;
}

.mt-2 {
  margin-top: 0.5rem;
}

.m-4 {
  margin: 1.5rem;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
